import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Lock, Timer, Trophy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAccount } from "wagmi";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchStakingInfo, stakeTokens } from "@/utils/stakingUtils";
import { formatEther } from "viem";

interface StakingPanelProps {
  coinId: string;
}

const StakingPanel = ({ coinId }: StakingPanelProps) => {
  const [stakingAmount, setStakingAmount] = useState("");
  const [lockPeriod, setLockPeriod] = useState("30");
  const { address } = useAccount();

  const { data: stakingInfo, isLoading } = useQuery({
    queryKey: ['staking', address, coinId],
    queryFn: () => fetchStakingInfo(address, coinId),
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const stakeMutation = useMutation({
    mutationFn: ({ amount, period }: { amount: string; period: string }) =>
      stakeTokens(amount, period),
    onSuccess: () => {
      toast.success(`Successfully staked ${stakingAmount} tokens for ${lockPeriod} days`);
      setStakingAmount("");
    },
    onError: (error) => {
      toast.error(`Failed to stake tokens: ${error.message}`);
    }
  });

  const calculateAPY = (days: string) => {
    const baseAPY = stakingInfo?.apy || 12;
    const bonus = (parseInt(days) / 30) * 2; // 2% extra APY per month
    return baseAPY + bonus;
  };

  const handleStake = () => {
    if (!stakingAmount) {
      toast.error("Please enter an amount to stake");
      return;
    }
    stakeMutation.mutate({ amount: stakingAmount, period: lockPeriod });
  };

  const formatTimeRemaining = (timestamp: number) => {
    const now = Date.now();
    const diff = timestamp - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (isLoading) {
    return (
      <div className="glass-card rounded-lg p-4 space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Lock className="h-4 w-4" />
          staking & rewards
        </h3>
        <div className="flex items-center gap-2 text-xs">
          <Timer className="h-3 w-3" />
          <span>next reward in {formatTimeRemaining(stakingInfo?.nextRewardTimestamp || Date.now())}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground">Amount to Stake</label>
          <Input
            type="number"
            placeholder="Enter amount"
            value={stakingAmount}
            onChange={(e) => setStakingAmount(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground">Lock Period (days)</label>
          <Input
            type="number"
            placeholder="30"
            value={lockPeriod}
            onChange={(e) => setLockPeriod(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-muted/50 rounded-lg p-3">
        <div className="flex justify-between text-sm mb-2">
          <span>Current APY</span>
          <span className="font-medium text-primary">{calculateAPY(lockPeriod)}%</span>
        </div>
        <Progress value={33} className="h-1" />
        <div className="text-xs text-muted-foreground mt-2 space-y-1">
          <p>Longer lock periods earn higher rewards</p>
          <p>Total Staked: {formatEther(stakingInfo?.totalStaked || BigInt(0))} tokens</p>
          <p>Your Stake: {formatEther(stakingInfo?.userStaked || BigInt(0))} tokens</p>
          <p>Available Rewards: {formatEther(stakingInfo?.rewards || BigInt(0))} tokens</p>
        </div>
      </div>

      <Button 
        onClick={handleStake} 
        className="w-full"
        disabled={stakeMutation.isPending}
      >
        {stakeMutation.isPending ? "Staking..." : "Stake Tokens"}
      </Button>
    </div>
  );
};

export default StakingPanel;